import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiShield, FiMail, FiLock, FiUser, FiGlobe, FiArrowRight } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import api from '../lib/api';
import toast from 'react-hot-toast';

export default function AuditorEnrollment() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [formData, setFormData] = useState({ fullName: '', email: '', password: '' });
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      toast.error('Missing enrollment token');
      navigate('/signin');
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const { data } = await api.post('/auth/auditor/verify-token', { token });
      setTenantInfo(data);
    } catch (error) {
      toast.error('Invalid or expired token');
      navigate('/signin');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegistering(true);
    try {
      const { data } = await api.post('/auth/auditor/register', { ...formData, token });
      localStorage.setItem('token', data.token);
      toast.success('Auditor access granted');
      navigate('/compliance');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-b-2 border-primary-500 rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-[2.5rem] p-10 shadow-2xl shadow-primary-500/5">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-primary-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-primary-500/20">
            <SafeIcon icon={FiShield} className="text-primary-500 text-4xl" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2 tracking-tight">Auditor Enrollment</h1>
          <p className="text-slate-400">Verifying access for <span className="text-primary-400 font-bold">{tenantInfo?.tenantName}</span></p>
        </div>

        <form onSubmit={handleRegister} className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 ml-1 uppercase tracking-widest">Full Name</label>
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input type="text" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} className="w-full pl-12 pr-4 py-4 bg-slate-800 border-slate-700 border rounded-2xl focus:ring-2 focus:ring-primary-500 text-white font-medium transition-all" placeholder="John Doe" required />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 ml-1 uppercase tracking-widest">Work Email</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full pl-12 pr-4 py-4 bg-slate-800 border-slate-700 border rounded-2xl focus:ring-2 focus:ring-primary-500 text-white font-medium transition-all" placeholder="auditor@firm.com" required />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 ml-1 uppercase tracking-widest">Secure Password</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full pl-12 pr-4 py-4 bg-slate-800 border-slate-700 border rounded-2xl focus:ring-2 focus:ring-primary-500 text-white font-medium transition-all" placeholder="••••••••" required />
            </div>
          </div>

          <div className="bg-slate-800/50 border border-slate-700 p-4 rounded-2xl flex items-center space-x-3 mb-6">
            <SafeIcon icon={FiGlobe} className="text-emerald-500" />
            <div className="text-xs text-slate-400">
              <p className="font-bold text-slate-300">Regional Data Pinning Active</p>
              <p>Session data will be pinned to Ireland (EU-West-1)</p>
            </div>
          </div>

          <button disabled={registering} className="w-full bg-primary-600 text-white py-5 rounded-2xl font-black shadow-lg shadow-primary-900/20 hover:bg-primary-500 transition-all flex items-center justify-center gap-2 group">
            {registering ? 'Activating Account...' : 'Initialize Secure Audit Access'}
            <motion.div animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
              <SafeIcon icon={FiArrowRight} />
            </motion.div>
          </button>
        </form>
      </motion.div>
    </div>
  );
}